import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { FilaDeLecturaPrixia } from '../types/prixia.types';
import {
  ESTADO_DE_LECTURA,
  TIPO_DE_LECTURA,
  VARIANTE_DE_LECTURA,
  duracion,
  fechaYHora,
} from '../lib/prixia-textos';

interface Props {
  lecturas: FilaDeLecturaPrixia[];
  mostrarTienda?: boolean;
  onCancelar?: (l: FilaDeLecturaPrixia) => void;
  /** Mientras se cancela una lectura, los botones de cancelar no responden (doble clic). */
  ocupado?: boolean;
  /** Qué decir cuando no hay ninguna (la ficha habla de «esta tienda»). */
  vacio?: string;
}

/**
 * Las lecturas de Prixia con QUIÉN las pidió (C10: sólo la plataforma lo ve) y el aviso
 * o el error de cada una (291 B6: «demasiados agotados» es la nota de la lectura).
 */
export function TablaDeLecturasPrixia({
  lecturas,
  mostrarTienda = false,
  onCancelar,
  ocupado = false,
  vacio = 'No hay lecturas en este período.',
}: Props) {
  if (lecturas.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{vacio}</p>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {mostrarTienda && <TableHead>Tienda</TableHead>}
          <TableHead>Lectura</TableHead>
          <TableHead>Estado</TableHead>
          <TableHead>Quién la pidió</TableHead>
          <TableHead>Pedida</TableHead>
          <TableHead>Duración</TableHead>
          <TableHead className="text-right">Leídos</TableHead>
          <TableHead className="text-right">Precios nuevos</TableHead>
          <TableHead className="text-right">No encontrados</TableHead>
          <TableHead>Aviso o error</TableHead>
          {onCancelar && <TableHead className="text-right">Acciones</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {lecturas.map((l) => {
          const sumados = l.pedidos - 1;
          return (
            <TableRow key={l.id}>
              {mostrarTienda && <TableCell className="font-medium">{l.storeName ?? '—'}</TableCell>}
              <TableCell>{TIPO_DE_LECTURA[l.type]}</TableCell>
              <TableCell>
                <Badge variant={VARIANTE_DE_LECTURA[l.status]}>{ESTADO_DE_LECTURA[l.status]}</Badge>
              </TableCell>
              <TableCell className="text-sm">
                {l.origen}
                {sumados > 0 && (
                  <span className="block text-xs text-muted-foreground">
                    {`${sumados} ${sumados === 1 ? 'pedido sumado' : 'pedidos sumados'}${
                      l.empresasQuePidieron.length > 0
                        ? ` · ${l.empresasQuePidieron.join(', ')}`
                        : ''
                    }`}
                  </span>
                )}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {fechaYHora(l.queuedAt)}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {duracion(l.startedAt, l.finishedAt)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {l.itemsRead.toLocaleString('es-BO')}
                {(l.noResponse ?? 0) > 0 && (
                  <span className="block text-xs text-amber-600">
                    {`${l.noResponse!.toLocaleString('es-BO')} sin respuesta`}
                  </span>
                )}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {l.pricePointsWritten.toLocaleString('es-BO')}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {l.notFound.toLocaleString('es-BO')}
              </TableCell>
              <TableCell className="max-w-[18rem] text-xs">
                {l.errorMessage ? (
                  <span className="text-destructive">{l.errorMessage}</span>
                ) : l.note ? (
                  <span className="text-amber-600">{l.note}</span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              {onCancelar && (
                <TableCell className="text-right">
                  {l.puedeCancelar && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={ocupado}
                      onClick={() => onCancelar(l)}
                      aria-label={`Cancelar la lectura de ${l.storeName ?? 'la tienda'}`}
                    >
                      Cancelar
                    </Button>
                  )}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

export default TablaDeLecturasPrixia;
